import React, { useEffect, useRef, useState } from "react";
import "./overflow-menu.css";
import { DotsVerticalIconV1 } from "images/image-component";

export interface OverflowMenuAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface Props {
  actions: OverflowMenuAction[];
  ariaLabel?: string;
}

function OverflowMenu({ actions, ariaLabel = "More actions" }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Mirrors the outside-click + Escape dismissal SearchableDropdown already uses.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const runAction = (action: OverflowMenuAction) => {
    setOpen(false);
    action.onClick();
  };

  return (
    <div className="om-root" ref={rootRef}>
      <button
        type="button"
        className="om-trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <DotsVerticalIconV1 size={16} />
      </button>

      {open && (
        <div className="om-menu" role="menu">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              className="om-item"
              disabled={action.disabled}
              onClick={() => runAction(action)}
            >
              {action.icon && <span className="om-item-icon">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default OverflowMenu;
